import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Save, Plus, Trash2, RotateCcw } from 'lucide-react';
import { footerApi } from '../../utils/api';
import { refreshSiteSettings } from '../../hooks/useSiteSettings';
import { FOOTER_DEFAULT_BG as MININAV_BG_COLOR } from '../layout/Footer';

function AdminFooter() {
  const [settings, setSettings] = useState({
    bg_color: MININAV_BG_COLOR,
    copyright: '© २०८३ Kalo Jhola News। सर्वाधिकार सुरक्षित।',
    editor_name: '',
    darta_number: '',
    about_text: 'निष्पक्ष, विश्वसनीय र समयमै — नेपालको मुटुबाट।'
  });
  const [team, setTeam] = useState([]);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await footerApi.getSettings();
      setSettings(response.data || {});
      try {
        const arr = JSON.parse(response.data?.team_members || '[]');
        setTeam(Array.isArray(arr) ? arr : []);
      } catch {
        setTeam([]);
      }
      setLogoPreview(response.data?.footer_logo || '');
    } catch (error) {
      console.error('Error fetching footer settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setRemoveLogo(false);
  };

  const handleLogoRemove = () => {
    setLogoFile(null);
    setLogoPreview('');
    setRemoveLogo(true);
    handleChange('footer_logo', '');
  };

  const updateTeamMember = (i, field, value) => {
    setTeam(team.map((m, idx) => (idx === i ? { ...m, [field]: value } : m)));
  };

  const addTeamMember = () => setTeam([...team, { name: '', role: '' }]);

  const removeTeamMember = (i) => setTeam(team.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = { ...settings, team_members: JSON.stringify(team) };
      if (removeLogo) payload.footer_logo = '';
      await footerApi.updateSettings(payload, logoFile);
      toast.success('फुटर सेटिङहरू सफलतापूर्वक अद्यावधिक गरियो।');
      // Footer live update — cache clear garera sabai mounted hooks refresh
      await refreshSiteSettings();
      await fetchSettings();
      setLogoFile(null);
      setRemoveLogo(false);
    } catch (error) {
      console.error('Error updating footer settings:', error);
      toast.error('अद्यावधिक गर्न असफल भयो। कृपया पुनः प्रयास गर्नुहोस्।');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-muted">लोड हुँदै...</div>;
  }

  const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft';
  const inputCls = 'w-full border border-line rounded-lg px-3 py-2.5 text-sm font-normal text-ink outline-none bg-white focus:border-crimson';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <h2 className="font-display text-2xl">फुटर सेटिङहरू</h2>
        <button className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-5 py-2.5 rounded-full hover:bg-crimson-dark disabled:opacity-60" onClick={handleSubmit} disabled={saving}>
          <Save size={15} /> {saving ? 'सुरक्षित गर्दै...' : 'सुरक्षित गर्नुहोस्'}
        </button>
      </div>

      {/* Branding */}
      <div className="bg-white border border-line rounded-[14px] p-5 md:p-6 mb-5">
        <h3 className="font-display text-lg mb-4">ब्रान्डिङ</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className={labelCls}>
            फुटर लोगो
            <div className="flex items-start gap-3">
              <input type="file" accept="image/*" onChange={handleLogoChange} className={`${inputCls} py-2`} />
              {logoPreview && (
                <div className="flex flex-col items-center gap-1">
                  <img src={logoPreview} alt="Footer logo" className="h-12 w-auto max-w-[120px] object-contain border border-line rounded-lg p-1" />
                  <button type="button" onClick={handleLogoRemove} className="text-[12px] font-bold text-crimson hover:underline">
                    हटाउनुहोस्
                  </button>
                </div>
              )}
            </div>
          </label>
          <label className={labelCls}>
            दर्ता प्रमाणपत्र नम्बर
            <input className={inputCls} value={settings.darta_number || ''} onChange={(e) => handleChange('darta_number', e.target.value)} placeholder="ज.नं. १२३४५६७८९" />
          </label>
          <label className={labelCls}>
            प्रेस काउन्सिल नम्बर
            <input className={inputCls} value={settings.press_council || ''} onChange={(e) => handleChange('press_council', e.target.value)} placeholder="प्रेस काउन्सिल दर्ता नं. १२३४" />
          </label>
          <label className={labelCls}>
            कम्पनी दर्ता नम्बर
            <input className={inputCls} value={settings.company_reg_number || ''} onChange={(e) => handleChange('company_reg_number', e.target.value)} placeholder="कम्पनी रजिस्ट्रेशन नं. १२३४५६/७८" />
          </label>
          <label className={labelCls}>
            सम्पादकको नाम
            <input className={inputCls} value={settings.editor_name || ''} onChange={(e) => handleChange('editor_name', e.target.value)} placeholder="सम्पादकको पूरा नाम" />
          </label>
          <label className={labelCls}>
            फुटर पृष्ठभूमि रङ
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="color"
                value={settings.bg_color || MININAV_BG_COLOR}
                onChange={(e) => handleChange('bg_color', e.target.value)}
                className="w-11 h-11 rounded-lg border border-line cursor-pointer bg-white p-1"
                aria-label="Background color"
              />
              <input className={inputCls} value={settings.bg_color || ''} onChange={(e) => handleChange('bg_color', e.target.value)} placeholder="#d1fae5 (मिनी नेभ रङ)" />
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-ink-soft border border-line rounded-lg hover:bg-paper-soft hover:border-emerald-400 transition-colors"
                onClick={() => handleChange('bg_color', MININAV_BG_COLOR)}
                title="मिनी नेभ/नेभबार जस्तो रङ प्रयोग गर्नुहोस्"
              >
                <RotateCcw size={13} /> डिफल्ट
              </button>
            </div>
          </label>
          <label className={labelCls}>
            कपीराइट पाठ
            <input className={inputCls} value={settings.copyright || ''} onChange={(e) => handleChange('copyright', e.target.value)} placeholder="© २०८३ Kalo Jhola News। सर्वाधिकार सुरक्षित।" />
          </label>
        </div>
      </div>

      {/* About & socials */}
      <div className="bg-white border border-line rounded-[14px] p-5 md:p-6 mb-5">
        <h3 className="font-display text-lg mb-4">हाम्रोबारे र सामाजिक सञ्जाल</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className={`${labelCls} md:col-span-2`}>
            हाम्रोबारे पाठ
            <textarea rows={4} className={`${inputCls} resize-y`} value={settings.about_text || ''} onChange={(e) => handleChange('about_text', e.target.value)} placeholder="कम्पनीको बारेमा जानकारी" />
          </label>
          {[
            ['facebook', 'Facebook URL'],
            ['twitter', 'Twitter URL'],
            ['instagram', 'Instagram URL'],
            ['youtube', 'YouTube URL'],
            ['whatsapp', 'WhatsApp नम्बर (जस्तै: 97798XXXXXXXX) वा पूरा लिंक'],
          ].map(([key, label]) => (
            <label key={key} className={labelCls}>
              {label}
              <input
                className={inputCls}
                value={settings[key] || ''}
                onChange={(e) => handleChange(key, e.target.value)}
                placeholder={key === 'whatsapp' ? '97798XXXXXXXX वा https://wa.me/97798XXXXXXXX' : `https://${key}.com/...`}
              />
            </label>
          ))}
        </div>

        {/* Share bar links — article page ko सेयर गर्नुहोस् icons */}
        <div className="mt-5 pt-5 border-t border-line">
          <h4 className="font-display text-[15px] font-bold text-ink mb-3">सेयर बटन लिंकहरू (लेख पेज)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ['share_facebook', 'Facebook सेयर लिंक'],
              ['share_twitter', 'Twitter/X सेयर लिंक'],
              ['share_whatsapp', 'WhatsApp सेयर लिंक'],
              ['share_viber', 'Viber सेयर लिंक'],
            ].map(([key, label]) => (
              <label key={key} className={labelCls}>
                {label}
                <input
                  className={inputCls}
                  value={settings[key] || ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  placeholder={`खाली छोडे default ${key.split('_')[1]} share प्रयोग हुन्छ`}
                />
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Team members */}
      <div className="bg-white border border-line rounded-[14px] p-5 md:p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h3 className="font-display text-lg">टिम सदस्यहरू</h3>
          <button type="button" onClick={addTeamMember} className="inline-flex items-center gap-1.5 bg-crimson/10 text-crimson font-bold text-[13px] px-4 py-2 rounded-full hover:bg-crimson hover:text-white transition-colors">
            <Plus size={15} /> सदस्य थप्नुहोस्
          </button>
        </div>
        {team.length === 0 ? (
          <p className="text-muted text-[13px]">कुनै टिम सदस्य थपिएको छैन।</p>
        ) : (
          <div className="flex flex-col gap-3">
            {team.map((m, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
                <label className={labelCls}>
                  नाम
                  <input className={inputCls} value={m.name || ''} onChange={(e) => updateTeamMember(i, 'name', e.target.value)} placeholder="सदस्यको नाम" />
                </label>
                <label className={labelCls}>
                  पद
                  <input className={inputCls} value={m.role || ''} onChange={(e) => updateTeamMember(i, 'role', e.target.value)} placeholder="प्रधान सम्पादक" />
                </label>
                <button type="button" aria-label={`सदस्य ${i + 1} हटाउनुहोस्`} onClick={() => removeTeamMember(i)} className="p-2.5 rounded-full border border-line text-ink-soft hover:text-crimson hover:border-crimson mb-0.5">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminFooter;
